// The presentational pieces of the Cattle Ticker homepage, per
// design/Main.dc.html. Grouped in one file because each is a handful of markup
// with no state — splitting them into seven files would be filing, not
// structure.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { SITE_PLAN } from "@/lib/sitePlan";

import type { FeederFlashEpisode } from "@/lib/rbFeederFlash";
import type { RbIndexLive, RbIndexTile } from "@/lib/rbIndexes";
import { lockButton } from "@/lib/indexAccess";
import type { Access } from "@/lib/siteAccess";

import LiveBeat from "./LiveBeat";
import LockMark from "./LockMark";
import { useFlash } from "./useFlash";
import type { HomeKpi } from "@/lib/rbHomeKpis";
import type { RbToolRow } from "@/lib/rbTools";
import { resolveToolIcon } from "../rbIcons";
import { ringsSentence } from "./ringsSentence";
import type {
  BoxedBeef,
  FiveArea,
  MarketStatus,
  Move,
  PackerMargin,
  TickerLot,
} from "@/lib/rbCattleTicker";

// ── The statement band ──────────────────────────────────────────────────────

/**
 * "What cattle are bringing. Not what they brought."
 *
 * Seven words that state the competitive difference without overclaiming:
 * everyone else publishes yesterday's average, which is what a summary IS.
 * This publishes the ring.
 *
 * `rings` is the barn count — the number of rooms actually selling right now,
 * which is the figure that does the bragging instead of an adjective.
 */
export function StatementBand({
  rings,
  status,
  day,
  ringsLabel,
}: {
  rings: string | null;
  /**
   * Say it outright, in the biggest type on the page.
   *
   * ⚠️ WHAT THIS REPLACED, AND WHY THE OLD OBJECTION DID NOT HOLD.
   * The default used to be the fixed string "rings live now", with a note here
   * saying it MUST NOT depend on open/closed because the homepage is
   * prerendered and a status-aware label would differ between build time and
   * hydration. The consequence was a Saturday homepage reading "24 RINGS LIVE
   * NOW" in 24-point type two inches under a lamp that said CLOSED. The state
   * board had already hit the same thing and worked around it locally.
   *
   * The objection was aimed at the wrong thing. A prerender mismatch comes from
   * the FIRST CLIENT RENDER differing from the server's HTML — and `status`
   * here is null on the server AND on that first paint, resolving only in an
   * effect afterwards. That is exactly how `TickerHead`'s Live lamp has always
   * worked on this same page. So the band can be honest, and the only real
   * constraint is that the UNKNOWN state must read true in both worlds.
   *
   * Owner call 2026-09-13: say live or closed, and always carry the date of the
   * cattle being shown — on a Saturday you are looking at Friday's sale and the
   * page should say so without making you hunt for it.
   */
  status?: MarketStatus | null;
  /** The ISO day the board is showing — `TickerPayload.day`. */
  day?: string | null;
  /** Last word, for a caller that needs its own wording. */
  ringsLabel?: string;
}) {
  return (
    <div className="rbtx-statement">
      <div className="rbtx-statement-left">
        {/* The DVAuction mark used to sit here, ahead of the sentence. Removed
            on owner's call — the statement is the product's claim and it reads
            stronger without a logo in front of it.
            ⚠️ ATTRIBUTION IS NOT LOST: the mark is still in the masthead, at
            the top of every one of these pages, which is where the artboard
            put the partner credit in the first place. If that ever comes out
            too, the data credit has to be re-homed rather than simply dropped. */}
        <p className="rbtx-statement-line">
          What cattle are bringing. <b>Not what they brought.</b>
        </p>
      </div>

      {rings && (
        <div className="rbtx-rings">
          <div className="rbtx-rings-n">{rings}</div>
          {/* Plural-aware. A NATIONAL board is never down to one barn, but a
              state board frequently is, and "1 RINGS" is the sort of detail
              that tells a cattleman nobody checked the page. Reads off `rings`,
              which the prerender and the browser both have, so the noun cannot
              differ between the two passes. */}
          <div className="rbtx-rings-l">{ringsLabel ?? ringsSentence(rings, status, day)}</div>
        </div>
      )}
    </div>
  );
}

// ── The crawling tape ───────────────────────────────────────────────────────

/**
 * Every print, in arrival order, crawling.
 *
 * The list is rendered TWICE on purpose. The crawl animates from 0 to -50%, so
 * at the halfway point the second copy sits exactly where the first started and
 * the loop is seamless. One copy would visibly snap back to the start.
 *
 * It pauses on hover (see the CSS) because a price you cannot stop to read is
 * decoration, and stops entirely under prefers-reduced-motion.
 */
export function TapeCrawl({
  lots,
  showClass,
}: {
  lots: TickerLot[];
  /** Mixed view. The tape shows prints from every band in arrival order, so on
   *  a mixed tab it is MORE confusing than the board without the class — the
   *  rows are not even grouped by anything. */
  showClass?: boolean;
}) {
  if (!lots.length) return null;
  // Enough to fill the strip at any width without the DOM getting silly. The
  // artboard runs 8 distinct prints, doubled.
  const run = lots.slice(0, 12);

  return (
    <div className="rbtx-tape">
      <div className="rbtx-tape-tag">
        <i aria-hidden="true" />
        <span>Tape</span>
      </div>
      {/* aria-hidden: every one of these prints is already on the board below in
          a readable, non-moving form, so a screen reader gains nothing from the
          duplicate and a crawling marquee is hostile to one. */}
      <div className="rbtx-tape-run" aria-hidden="true">
        {[0, 1].map((copy) =>
          run.map((lot) => (
            <span className="rbtx-tape-lot" key={`${copy}-${lot.id}`}>
              <span className="rbtx-tape-barn">{lot.barn}</span>
              <span className="rbtx-tape-spec">
                {lot.headLabel} · {lot.weightLabel}
              </span>
              {showClass && lot.classLabel && (
                <span className="rbtx-tape-class" data-class={lot.cattleType}>
                  {lot.classLabel}
                </span>
              )}
              <span className="rbtx-tape-px">{lot.priceLabel}</span>
            </span>
          )),
        )}
      </div>
    </div>
  );
}

// ── Today's picks ───────────────────────────────────────────────────────────

/**
 * The day's curated tools, as a SECONDARY MENU directly under the masthead.
 *
 * ⚠️ IT USED TO BE A NAVY CARD STRIP UNDER THE BOARD, and it was being missed
 * (owner, 2026-09-13): "turn it into a secondary menu under the main menu that
 * is a different color so that it is easier to see and not get missed." Gold
 * on navy chrome is the loudest contrast the palette has, and a menu bar is
 * where a visitor already looks for somewhere to go. Names only — a menu is
 * scanned, not read, so the descriptions ride in each link's title.
 *
 * `weekday` is null on the server and on the first client render, because the
 * build machine's day is not the visitor's. The label reads "Today's picks"
 * until mount and then names the day; its min-width keeps the links from
 * jumping sideways when it does.
 */
export function PicksMenu({
  weekday,
  picks,
  curated,
  variant = "bar",
}: {
  weekday: string | null;
  picks: RbToolRow[];
  /** "bar": the gold strip under the masthead (2026-09-13). "rail": the
   *  same picks stacked in a gold card for the right sidebar, where the index
   *  list used to be (owner, 2026-10-08: "move the today's top picks to the
   *  right sidebar in their place"). A column cannot overflow sideways, so the
   *  scroll buttons never appear there. */
  variant?: "bar" | "rail";
  /**
   * Did anything actually rise to the top TODAY?
   *
   * ⚠️ WHEN FALSE THIS MENU IS NOT A CURATION, and the label must not claim
   * it is. `rb_tool_schedule` is scheduled per weekday, and on 2026-09-13 it
   * held 26 rows covering Monday through Saturday and NONE for Sunday — so a
   * Sunday visitor got "SUNDAY / TODAY'S PICKS" over the first six tools in
   * registry order. That promise-without-a-curation is a documented repeat:
   * the homepage rendered "Monday's Top Tools" for months off a `featured`
   * field that nothing ever set. See tools.ts.
   */
  curated: boolean;
}) {
  // ── The strip scrolls sideways, with buttons (owner, 2026-09-27) ────────
  // ⚠️ THIS REVERSES A 2026-09-14 DECISION, AND THE REASON MATTERS. It used to
  // scroll with its scrollbar HIDDEN and nothing else to say more existed, so
  // in a narrow window the last picks simply sat past the edge — the owner's
  // words were "today's top picks get cut off". It was changed to wrap.
  // Buttons answer the original complaint directly: the overflow is now
  // announced by a control rather than left to be discovered. That is why the
  // arrows are always rendered when the strip overflows and are DISABLED at
  // each end rather than hidden — a button that vanishes tells you nothing.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [nudge, setNudge] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    // 2px of slack: sub-pixel layout means scrollLeft rarely hits the exact end.
    const max = el.scrollWidth - el.clientWidth;
    setNudge({ left: el.scrollLeft > 2, right: el.scrollLeft < max - 2 });
  }, []);

  useEffect(() => {
    measure();
    const el = listRef.current;
    if (!el) return;
    // ResizeObserver as well as scroll: the strip also overflows when the
    // WINDOW narrows, with no scroll event to notice it.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, picks.length]);

  const scrollBy = (dir: -1 | 1) => {
    const el = listRef.current;
    if (!el) return;
    // Step by WHOLE CARDS, measured off the first one, rather than a fixed
    // pixel guess: the cards are content-width, so a guess lands mid-card and
    // the snap then yanks the strip somewhere the click did not ask for.
    const card = el.firstElementChild as HTMLElement | null;
    const gap = 9;
    const step = card ? card.getBoundingClientRect().width + gap : 240;
    const n = Math.max(1, Math.floor(el.clientWidth / step));
    el.scrollBy({ left: dir * step * n, behavior: "smooth" });
  };

  if (!picks.length) return null;

  return (
    <nav className="rbtx-picksmenu" data-variant={variant} aria-label={curated ? "Today's picks" : "Popular tools"}>
      <div className="rbtx-picksmenu-in">
        <div className="rbtx-picksmenu-label">
          <svg className="rbtx-pick-star" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3.2l2.6 5.5 6 .8-4.4 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9L3.4 9.5l6-.8z" />
          </svg>
          <span>
            {/* ⚠️ THE STRINGS ARE LOAD-BEARING. picksStripLabel.test.tsx
                asserts this exact text, and asserts the word "picks" is ABSENT
                when nothing was scheduled — because a lineup that is merely the
                first six tools in registry order must not claim to be a
                curation. The caps in the artwork are done in CSS so the DOM
                text stays as the test reads it. */}
            <b>{curated ? `${weekday ?? "Today"}'s picks` : "Popular tools"}</b>
            <em>{curated ? "Chosen for today" : "Most opened this week"}</em>
          </span>
        </div>
        <div
          className="rbtx-picksmenu-list"
          ref={listRef}
          onScroll={measure}
          /* Fade only the edge that actually continues — a fade on a
             fully-scrolled end would dim a card for no reason. */
          data-more-l={nudge.left ? "1" : undefined}
          data-more-r={nudge.right ? "1" : undefined}
        >
          {picks.map((t) => {
            const Icon = resolveToolIcon(t.iconName);
            return (
              <a
                className="rbtx-pickcard"
                key={t.slug}
                href={t.href}
                title={t.description ?? undefined}
              >
                <span className="rbtx-pickcard-ic" aria-hidden="true">
                  <Icon size={17} strokeWidth={2} />
                </span>
                <span className="rbtx-pickcard-t">
                  <b>
                    {/* The live dot only on a tool that is itself live now. */}
                    {/live/i.test(t.name) && <i className="rbtx-pick-live" aria-hidden="true" />}
                    {t.name}
                  </b>
                  <em>{t.category}</em>
                </span>
                <span className="rbtx-pickcard-go" aria-hidden="true">→</span>
              </a>
            );
          })}
        </div>

        {/* Rendered only when there is something to scroll to — on a wide
            screen with four picks these would be two dead controls. Never in
            the rail: a column does not scroll sideways, and a name wider
            than the column (seen on the 2026-10-08 preview: "OKC West Market
            Leaderboard") made the measure think it did and drew a dead
            button over the first pick. */}
        {variant === "bar" && (nudge.left || nudge.right) && (
          <div className="rbtx-picksnav">
            <button
              type="button"
              className="rbtx-picksnav-b"
              onClick={() => scrollBy(-1)}
              disabled={!nudge.left}
              aria-label="Scroll picks left"
            >
              ‹
            </button>
            <button
              type="button"
              className="rbtx-picksnav-b"
              onClick={() => scrollBy(1)}
              disabled={!nudge.right}
              aria-label="Scroll picks right"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

// ── Sale in progress ────────────────────────────────────────────────────────

/**
 * A flashing "SALE IN PROGRESS" banner on a barn's pages while its ring is
 * selling (owner, 2026-09-14: "on the live barn pages if the sale is live we
 * need to have a flashing banner that says sale in progress").
 *
 * The caller decides "live" from data, never a clock: the sale report's
 * `identity.status === "live"` (a lot within 90 minutes) and the barn ticker's
 * lamp (rpc_cattle_ticker_last_print, same 90 minutes). Render it only when
 * that is true — the component itself does not guess.
 *
 * The flash is the white dot and a soft green pulse of the band (green, not
 * red: "red means stop", owner 2026-09-14); under
 * prefers-reduced-motion it holds still and stays green.
 */
export function SaleInProgress({ detail }: { detail?: string | null }) {
  return (
    <div className="rbtx-saleon" role="status" aria-live="polite">
      <i className="rbtx-saleon-dot" aria-hidden="true" />
      <span className="rbtx-saleon-t">Sale in progress</span>
      {detail && <span className="rbtx-saleon-d">{detail}</span>}
    </div>
  );
}

// ── Index cards ─────────────────────────────────────────────────────────────

/** Sparkline path for a KPI's series, drawn into the artboard's 74×22 box. */
function sparkPoints(spark: number[]): string {
  if (spark.length < 2) return "0,22 74,22";
  const lo = Math.min(...spark);
  const hi = Math.max(...spark);
  const span = hi - lo || 1;
  return spark
    .map((v, i) => {
      const x = (i / (spark.length - 1)) * 74;
      const y = 22 - ((v - lo) / span) * 22;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/**
 * The three market indexes.
 *
 * ⚠️ EVERY TILE SHIPS ITS AS-OF DATE, always — not only while the feed is
 * broken. A stale price rendered bare is indistinguishable from a live one, and
 * this is the front door of the company. The NBW feed behind two of these has
 * been dead since 2026-07-26 (ACQUISITION_ROADMAP 3g), so right now they read
 * "as of Jul 26"; when it is repaired the stamp simply reads as today, which is
 * what a market terminal does anyway.
 */
/**
 * The latest Feeder Flash episode, in the square the index cards used to hold
 * (owner, 2026-09-25).
 *
 * ⚠️ A THUMBNAIL AND A LINK, NOT AN EMBED. A YouTube iframe on the front door
 * ships ~500 KB of Google JavaScript and sets Google cookies on every visitor
 * before anyone presses play — on a page whose whole document is 17 KB, and
 * for a video most visitors will not watch. The thumbnail is one image from
 * i.ytimg.com, and the click goes to YouTube where the player belongs.
 *
 * ⚠️ NO EPISODE MEANS NO CARD. Not a placeholder, not "no episode today". The
 * rail simply starts at the packer side, which is what it did yesterday.
 */
export function FeederFlashCard({ episode }: { episode: FeederFlashEpisode | null }) {
  if (!episode) return null;
  return (
    <section className="rbtx-ff">
      <div className="rbtx-ff-label">Feeder Flash</div>
      <a className="rbtx-ff-card" href={episode.url} target="_blank" rel="noopener noreferrer">
        <span className="rbtx-ff-thumb">
          {/* Lazy + async: it is below the board's fold on a phone and must not
              compete with the prices for the first paint. Dimensions are set so
              it reserves its own space and nothing shifts when it lands. */}
          <img
            src={episode.thumbnailUrl}
            alt=""
            width={480}
            height={360}
            loading="lazy"
            decoding="async"
          />
          <span className="rbtx-ff-play" aria-hidden="true" />
        </span>
        <span className="rbtx-ff-body">
          <span className="rbtx-ff-title">{episode.title}</span>
          <span className="rbtx-ff-meta">
            {episode.dayLabel} · watch on YouTube
          </span>
        </span>
      </a>
    </section>
  );
}

/**
 * The three DVA numbers, top of the rail (owner, 2026-09-25).
 *
 * ⚠️ ONE PANEL, THREE ROWS — NOT THREE CARDS. The retired NBW rail was three
 * side-by-side cards in a 396px column: ~120px each, a 19px figure, a
 * truncated label ("CME FEEDER CATTLE …") and a grey self-scaling squiggle.
 * The owner's note on this rebuild was "styled better than the last ones", and
 * the thing that was wrong was the shape: three cards fight the width, so the
 * figure has to shrink and the label has to be cut. Rows do not fight it. The
 * columns align, the numbers are tabular, and nothing is abbreviated.
 *
 * ⚠️ NO SPARKLINES. They were decoration at 74x22 and MarketKpi already says
 * so in its own header: "a 14px squiggle is decoration rather than
 * information." The Index row IS the trend; a squiggle beside it adds nothing
 * and costs the row its alignment. `spark` is still fetched and still carried
 * in the prerendered payload, so a larger treatment can be restored without
 * touching the data layer.
 *
 * ⚠️ THE HEAD COUNT IS NOT DECORATION. The live row can read $354.53 on 305
 * head at 10am while the settled market sits at $344 — a 3% overstatement,
 * quoted to the cent, on the front door of the company. The figure is allowed
 * to be thin because the depth is printed under it. Remove the head count and
 * the row starts lying.
 */
/**
 * What each number is, in a cattleman's words. Three figures from one cohort
 * look alike on a 396px rail, and the owner asked for an explanation on hover —
 * so this is the difference between the panel being three prices and being
 * three answers.
 *
 * ⚠️ CSS-ONLY, NO JAVASCRIPT. This markup ships inside a PRERENDERED document.
 * A React tooltip would cost state and a listener on every row for something
 * that :hover already does, and Radix is out entirely on this surface (portals
 * and layout effects in a tree rendered under Node). The text is in the DOM,
 * so a screen reader gets it whether or not anyone hovers.
 */
// ── The DVA cattle indexes ───────────────────────────────────────────────────

/**
 * Seven published benchmarks down the rail, each with its seven-month line.
 *
 * ⚠️ NO GRADIENT, NO GENERATED IDs, AND THAT IS NOT A STYLE CHOICE. This markup
 * ships inside a PRERENDERED document and is hydrated in the browser. An SVG
 * gradient needs a unique id, and the obvious way to make one — Math.random, a
 * counter — produces a different id on the server than in the browser, which is
 * a hydration mismatch React will throw on. The fill is a flat low-opacity
 * `currentColor` instead: deterministic, one less element, and the colour still
 * follows the row's direction because CSS sets it.
 *
 * ⚠️ UP IS GREEN. Cattle prices — up is good news for the man selling them,
 * same as the panel above. Opposite of the diesel page, where a rise is a cost.
 *
 * ⚠️ THE COVERAGE GRADE IS NEVER SHOWN BARE. "STRONG" beside a falling price
 * reads as "strong market", which is the opposite of the number next to it. It
 * describes the SAMPLE, so the word COVERAGE sits in front of it, on its own
 * line — at 320px the rail has vertical room and no horizontal room at all.
 */
/** ⚠️ THE LINE SHOWS THIRTY DAYS, NOT THE WHOLE RECORD, AND THAT IS A
 *  CORRECTNESS FIX RATHER THAN A STYLE ONE. It drew all 216 days and coloured
 *  itself by first-vs-last, so every tile was red — measured 2026-10-04:
 *
 *      index                  7d        30d     whole window
 *      Feeder Cattle       -1.99     +8.05          -26.43
 *      Feeder Steer        +0.22    +13.13          -21.26
 *      Calf               +10.43    +25.70          -39.53
 *      Steer & Heifer     + 9.87    +26.68          -31.62
 *      Cow                 -3.37    -21.97          -24.70
 *
 *  Every whole-window figure is negative, so every line was red — while FIVE
 *  OF SEVEN had risen over the last month, two of them by more than $25. A
 *  seven-month line was drawing the fall since July and hiding a month of
 *  recovery, directly above a green +0.17. The owner read it as a colour bug;
 *  it was the line answering a question nobody asked.
 *
 *  Thirty days matches what a tile is for — what the market is doing lately —
 *  and the colour is that same window's direction, so shape and colour cannot
 *  disagree. The index PAGE keeps the full series behind its range buttons,
 *  where the period is labelled and a long view is the point.
 */
const SPARK_DAYS = 30;

/** ⚠️ INSET BY THE STROKE. The path used to run 0..w and 1..h-1, so half of
 *  every edge stroke fell outside the viewBox and the line looked clipped at
 *  both ends — which is what "almost cut off" was. */
function sparkPath(prices: number[], w: number, h: number): { line: string; area: string } | null {
  if (prices.length < 2) return null;
  const M = 2;
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  const span = hi - lo || 1;
  const x = (i: number) => M + (i / (prices.length - 1)) * (w - M * 2);
  const y = (v: number) => h - M - ((v - lo) / span) * (h - M * 2);
  const line = prices.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  return { line, area: `${line} L${(w - M).toFixed(1)} ${h} L${M} ${h} Z` };
}

export function RbIndexCards({
  tiles,
  live,
  updatedAt,
  variant = "rail",
  cta = "loading",
}: {
  tiles: RbIndexTile[];
  /** Who is looking, for the button under locked cards (Premium lock,
   *  2026-10-13). Ignored while the cards carry prices. */
  cta?: Access["state"];
  /** "rail": the stacked list the right sidebar carried from 2026-10-04.
   *  "band": the same six, as cards across the top of the homepage under the
   *  masthead (owner, 2026-10-08, from a design mock: "the indexes that are on
   *  the right sidebar now, going across the top"). Same data, same rows,
   *  same words — only the frame and the sparkline's width differ. */
  variant?: "rail" | "band";
  /** The moment of the last successful live fetch — see LiveBeat. */
  updatedAt?: number | null;
  /** Today's trade so far, by slug. Absent until the first fetch lands — the
   *  row simply is not drawn, which is also what a day with no sales looks
   *  like. Deliberately NOT baked by the prerender: this document is served
   *  until the next deploy and a stale "live" price is worse than none. */
  live?: Record<string, RbIndexLive>;
}) {
  // The band's clock, client-only: the prerender has no visitor's time, so
  // the server renders the slot empty and the browser fills it after mount
  // (same rule as PicksMenu's weekday). Central, because the sales are.
  const [clock, setClock] = useState<string | null>(null);
  useEffect(() => {
    if (variant !== "band") return;
    const tick = () => setClock(
      new Date().toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" }).replace(" at ", " · ") + " CT",
    );
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [variant]);
  if (tiles.length === 0) return null;
  const band = variant === "band";
  const locked = tiles.some((t) => t.locked);
  return (
    <section className="rbtx-rbix" data-variant={variant}>
      {band ? (
        // The band's header, matched to the mock: a display title, one line of
        // what these are, the heartbeat, and the way to all of them.
        // The same head the Cattle Ticker wears (owner, 2026-10-08: "look how
        // the cattle ticker blue header goes the full width… make it look
        // like that as well"): navy, gold rule, the display face, the last
        // word in gold. The cards sit in the same ground panel the board does.
        <div className="rbtx-rbix-head">
          <div className="rbtx-rbix-headl">
            <span className="rbtx-rbix-title">RaisedBeef Market <b>Indexes</b></span>
            <span className="rbtx-rbix-sub">Proprietary, real-time benchmarks from live U.S. auction transactions.</span>
          </div>
          <div className="rbtx-rbix-headr">
            <span className="rbtx-rbix-when">
              <LiveBeat at={updatedAt ?? null} />
              {clock && <span className="rbtx-rbix-clock">{clock}</span>}
            </span>
            <a className="rbtx-rbix-all" href="/index">View all indexes <span aria-hidden="true">→</span></a>
          </div>
        </div>
      ) : (
        <div className="rbtx-rbix-label">
          <span>Cattle Market Indexes</span>
          {/* ⚠️ THE PANEL SAYS IT IS ALIVE EVEN WHEN NO PRICE MOVED. A rolling
              seven-day index often does not reach the second decimal on a
              twenty-second tick — measured, two of the six headline indexes did
              not move at all over a full minute. Flashing them anyway would be a
              small lie; saying when we last heard back is the true thing. */}
          <LiveBeat at={updatedAt ?? null} />
        </div>
      )}
      <div className="rbtx-rbix-body">
        {/* The one line and the one button on a locked band, ABOVE the cards
            and small (owner, 2026-10-14: "put index prices are part of premium
            above the indexes along with the button and make it much smaller").
            The button's words follow who is looking (index-lock mock). */}
        {locked && cta !== "open" && (
          <div className="rbtx-rbix-cta">
            <p>Index prices are part of <b>{SITE_PLAN.short}</b>.</p>
            <a href="/membership">{lockButton(cta)}</a>
          </div>
        )}
        <div className="rbtx-rbix-box">
          {tiles.map((t) => (
            <RbIndexRow key={t.slug} t={t} live={live?.[t.slug]} band={band} />
          ))}
        </div>
      {/* ⚠️ SAID OUT LOUD, AND IT IS THE CREDIBILITY CLAIM. CME's own Feeder
          Cattle Index is also a 7-day weighted average — ours recalculates
          through the day rather than once overnight, which is the difference
          worth claiming. The other deliberate one is head vs pounds, §2.1. */}
        <p className="rbtx-rbix-source">
          RaisedBeef calculation from auction sales · head-weighted, trailing 7
          days including today, 11 states · updates every 20 seconds
        </p>
      </div>
    </section>
  );
}

/**
 * One index on the homepage rail.
 *
 * ⚠️ THE BIG NUMBER IS THE LIVE INDEX NOW, NOT LAST NIGHT'S CLOSE. Owner,
 * relaying Rob, 2026-10-05: "the actual index number is changing" through the
 * day. The close remains the fallback — before the first sale of the morning,
 * and all weekend, there is nothing newer, and yesterday's price beats a gap
 * where a price belongs.
 */
/** "2:14 PM", Central — the clock the sales run on. */
function clockCt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

/** ⚠️ ROLLING DIGITS (owner, 2026-10-08: "the rolling digits sounds cool").
 *  Each digit is a column of 0–9 that slides to the new digit when the value
 *  changes; "$", "," and "." stay put. The markup is a pure function of the
 *  text, so the prerender and the browser agree and hydration is clean. A
 *  screen reader gets the plain text from aria-label; the strips are hidden
 *  from it. tabular-nums on the value keeps every column the same width. */
const ODO_DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;
export function Odometer({ text }: { text: string }) {
  return (
    <span className="rbtx-odo" aria-label={text} role="text">
      {[...text].map((ch, i) =>
        /\d/.test(ch) ? (
          <span key={i} className="rbtx-odo-col" aria-hidden="true">
            <span className="rbtx-odo-strip" style={{ transform: `translateY(-${Number(ch) * 10}%)` }}>
              {ODO_DIGITS.map((d) => <span key={d}>{d}</span>)}
            </span>
          </span>
        ) : (
          <span key={i} className="rbtx-odo-ch" aria-hidden="true">{ch}</span>
        ),
      )}
    </span>
  );
}

function RbIndexRow({ t, live, band = false }: { t: RbIndexTile; live?: RbIndexLive; band?: boolean }) {
  // In the band the line ends on the live index (today's reading, or the
  // close when there is none) so the pulsing point at its end IS the number
  // on the card — not decoration (owner, 2026-10-08).
  const liveNum = band && !t.locked && live?.indexValue ? Number(live.indexValue.replace(/[$,]/g, "")) : null;
  // ⚠️ A LOCKED CARD'S LINE ARRIVES FINISHED: the server cut it to the same
  // thirty points and ended it on the live reading, as a 0–1 shape, because
  // the live point is a price this visitor does not get.
  const recent = t.locked
    ? t.spark
    : liveNum !== null && Number.isFinite(liveNum)
      ? [...t.spark.slice(-(SPARK_DAYS - 1)), liveNum]
      : t.spark.slice(-SPARK_DAYS);
  const lineLive = t.locked ? !!live?.hasIndex : liveNum !== null;
  // In the band the line runs the card's width. The path is drawn at 240×44
  // and stretched to fit; non-scaling-stroke keeps the line the same weight
  // however wide the card is, so a wide screen does not fatten it.
  const sw = band ? 240 : 112;
  const sh = band ? 44 : 32;
  const p = sparkPath(recent, sw, sh);
  // Where the line ends, as a share of the box's height, for the live point.
  const endY = (() => {
    if (recent.length < 2) return null;
    const lo = Math.min(...recent), hi = Math.max(...recent), span = hi - lo || 1;
    const M = 2;
    return ((sh - M - ((recent[recent.length - 1] - lo) / span) * (sh - M * 2)) / sh) * 100;
  })();
  // A flat market and a missing comparison are different things and
  // neither gets an arrow. "none" is the missing one.
  // ⚠️ EVERY FIGURE ON THE TILE COMES FROM THE SAME WINDOW. The price went
  // live before these did, and for a few hours the rail printed $343.64 beside
  // "14,297 hd" when that price was built on 11,219 — the depth is on the tile
  // precisely so a thin figure cannot pass for a deep one, and a mismatched
  // one does the opposite. Live where we have it, last night's close where we
  // do not (before the first sale, and all weekend).
  const head = live?.hasIndex ? live.indexHead : t.head;
  const states = live?.hasIndex && live.indexStates != null ? live.indexStates : t.states;
  const dayChange = live?.hasIndex ? live.dayChange : t.dayChange;
  const dayDirV = live?.hasIndex ? live.dayDir : t.dayDir;
  const weekChange = live?.hasIndex ? live.weekChange : t.weekChange;
  const weekDirV = live?.hasIndex ? live.weekDir : t.weekDir;
  const dir = dayDirV ?? (dayChange === null ? "none" : "flat");
  // Flash on the number, never the formatted string — "$342.30" and "$342.3"
  // are the same price and different text.
  const ixFlash = useFlash(live?.indexValue ? Number(live.indexValue.replace(/[$,]/g, "")) : null);
  const tdFlash = useFlash(live ? live.headCount : null);
  // The "i" in the card's corner (owner, 2026-10-08: "a little ? or i in the
  // upper right hand corner so when you click on it the tooltip opens from
  // there and not from hovering"). Click, not hover; Escape or a click
  // anywhere else closes it. The card itself stays a plain prerendered link.
  const [tip, setTip] = useState(false);
  useEffect(() => {
    if (!tip) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e instanceof MouseEvent && (e.target as Element | null)?.closest?.(".rbtx-rbix-infotip, .rbtx-rbix-info")) return;
      setTip(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("click", close);
    return () => { document.removeEventListener("keydown", close); document.removeEventListener("click", close); };
  }, [tip]);
  // Keep the note on screen (owner, 2026-11-03: "on the reduced screensize the tooltip on the index
  // gets cut off"). It hangs from the card's right edge, 300px wide; on the first card of a narrow
  // row that put its left side past the window. Measured once it opens and slid back inside an
  // 8px margin — before paint, so it never flashes in the wrong place.
  const tipRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!tip || !el) return;
    el.style.transform = "";
    const r = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const shift = r.left < 8 ? 8 - r.left : r.right > vw - 8 ? vw - 8 - r.right : 0;
    if (shift) el.style.transform = `translateX(${Math.round(shift)}px)`;
  }, [tip]);
  const card = (
            // A real <a>, not an onClick: this markup is PRERENDERED, so the
            // link has to work in the served HTML before any JavaScript runs —
            // and a crawler following it is how an index page gets indexed.
            <a className="rbtx-rbix-row" href={`/index/${t.slug}`}
               /* The whole card washes green or red on a change in the band
                  (owner, 2026-10-08, "look more alive"); the rail keeps the
                  wash behind the number only. */
               data-flash={band ? (ixFlash ?? undefined) : undefined}>
              {/* ⚠️ "RB " DROPPED HERE ONLY. The rail is already headed
                  "Cattle Market Indexes" and footed "RaisedBeef calculation",
                  so the prefix is said twice before this line and costs width
                  the longest name cannot spare: "DVA Real Time Steer & Heifer
                  500+ Index" is 38 characters, uppercase with letter-spacing,
                  in a 320px rail. The full formal name stays in the database
                  and is what the index page, the share card and Clara say. */}
              {/* "Real Time" stays in the name everywhere (owner, 2026-10-08:
                  "I want Real Time in the Names" — the band had dropped it
                  for an hour to match the mock). Only "RB " is dropped. */}
              <span className="rbtx-rbix-name">{t.name.replace(/^(RB|DVA) /, "")}{band && nameRange(t) && <span className="rbtx-rbix-wt"> ({nameRange(t)})</span>}</span>
              <span className="rbtx-rbix-val" data-flash={band ? undefined : (ixFlash ?? undefined)}
                    data-locked={t.locked ? "1" : undefined}>
                {t.locked ? <LockMark /> : band ? <Odometer text={live?.indexValue ?? t.value} /> : (live?.indexValue ?? t.value)}
              </span>
              <span className="rbtx-rbix-move" data-dir={dir}>
                {dayChange === null ? "—" : dayChange}
                {dayDirV && <> {dayDirV === "up" ? "▲" : "▼"}</>}
              </span>
              {p && band && (
                <span className="rbtx-rbix-sparkwrap">
                  <svg
                    className="rbtx-rbix-spark"
                    data-dir={recent[recent.length - 1] >= recent[0] ? "up" : "down"}
                    viewBox={`0 0 ${sw} ${sh}`}
                    width="100%"
                    height={sh}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path d={p.area} fill="currentColor" fillOpacity="0.14" />
                    <path d={p.line} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                  </svg>
                  {/* The live point: at the line's end, pulsing while the
                      reading is live, still when it is last night's close. */}
                  {endY !== null && (
                    <i className="rbtx-rbix-sparkdot" data-live={lineLive ? "1" : undefined}
                       data-dir={recent[recent.length - 1] >= recent[0] ? "up" : "down"}
                       style={{ top: `${endY.toFixed(1)}%` }} aria-hidden="true" />
                  )}
                </span>
              )}
              {p && !band && (
                <svg
                  className="rbtx-rbix-spark"
                  data-dir={recent[recent.length - 1] >= recent[0] ? "up" : "down"}
                  viewBox={`0 0 ${sw} ${sh}`}
                  width={band ? "100%" : sw}
                  height={sh}
                  preserveAspectRatio={band ? "none" : undefined}
                  aria-hidden="true"
                >
                  <path d={p.area} fill="currentColor" fillOpacity="0.14" />
                  <path d={p.line} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
                        vectorEffect={band ? "non-scaling-stroke" : undefined} />
                </svg>
              )}
              <span className="rbtx-rbix-meta">
                {head} hd · {states} states
                {weekChange && (
                  <>
                    {" · "}
                    <b data-dir={weekDirV ?? "flat"}>{weekChange} 7d</b>
                  </>
                )}
              </span>
              {/* ⚠️ THE HEAD COUNT IS NOT DECORATION — IT IS WHY THE PRICE IS
                  ALLOWED TO BE THIN. Measured over 14 days, only 3.7% of a sale
                  day's lots have arrived by 8am and 22% by 10am, so a live
                  figure at breakfast rests on a handful of pens. The panel this
                  replaces said it plainly: "the figure is allowed to be thin
                  because the depth is printed under it. Remove it and the row
                  lies." Nothing traded yet means NO ROW, not a zero. */}
              {/* ⚠️ "TODAY", NOT "LIVE". The headline above is live too now,
                  so labelling this one "Live" would say the other is not. They
                  are also genuinely different numbers — measured across all 18
                  on 2026-10-05, a median $6.53 apart and as much as $22.65 on a
                  thin index, because today alone can rest on a handful of
                  morning pens while the index has six settled days under it. */}
              {live?.hasToday && (
                <span className="rbtx-rbix-live" data-flash={tdFlash ?? undefined}>
                  <i className="rbtx-rbix-dot" aria-hidden="true" />
                  {/* "Wtd. Avg" (owner, 2026-11-03): the day's figure is today's lots,
                      head-weighted — sum(price × head) / sum(head) in rb_index_live_v2. */}
                  <b>{band ? "Today Wtd. Avg" : "Today"}</b>
                  {/* Locked: the day's price is Premium; the head and the
                      last lot still say the market is moving (option C). */}
                  {t.locked ? <em className="rbtx-rbix-tdlock"><LockMark label={null} /></em> : <em>{live.value}</em>}
                  <span>{live.head} hd</span>
                  {/* When the newest lot landed — the line that moves every
                      few minutes on a sale day (owner, 2026-10-08). Band only;
                      the rail has no room. */}
                  {band && live.lastAt && (
                    <span className="rbtx-rbix-lastlot">· last lot {clockCt(live.lastAt)}</span>
                  )}
                </span>
              )}
              {/* Same CSS-only pattern as the panel above: the sentence is in
                  the DOM either way, so a screen reader gets it without a
                  hover and the prerender costs no state. */}
              {/* The rail's hover note. ⚠️ NOT IN THE BAND: the band opens its note
                  from the "i" by click only (owner, 2026-10-08), and a hover note
                  under it is the "tooltip still appears when hovering" the owner
                  reported on 2026-10-13. */}
              {!band && (
                <span className="rbtx-rbix-tip" role="note">
                  {t.method}
                </span>
              )}
            </a>
  );
  if (!band) return card;
  return (
    <div className="rbtx-rbix-cardwrap" data-tip={tip ? "1" : undefined}>
      {card}
      <button type="button" className="rbtx-rbix-info" aria-label={`About the ${t.name}`} aria-expanded={tip}
              onClick={() => setTip((v) => !v)}>i</button>
      {tip && (
        // ⚠️ "infotip", NOT "tip". It carried the hover note's class, which is
        // hidden until the card is hovered, so a click opened an invisible
        // box and the "i" looked dead (found 2026-10-13).
        <div ref={tipRef} className="rbtx-rbix-infotip" role="dialog" aria-label={`About the ${t.name}`}>
          {/* Plain words, what each number on the card is (owner, 2026-11-03: "make this sound less AI
              and more specific on what we are actually showing in plain english"). The index page keeps
              t.method. */}
          <b>{t.name.replace(/^(RB|DVA) /, "")}{nameRange(t) ? ` (${nameRange(t)})` : ""}</b>
          <p>{plainAbout(t)}</p>
          <ul className="rbtx-rbix-infotip-keys">
            <li><b>Big number:</b> the index now, with today&rsquo;s sales in it. Before the first sale and on weekends it is the last close.</li>
            <li><b>Red or green:</b> the change since the last close.</li>
            <li><b>Line:</b> the last 30 days.</li>
            <li><b>Under the line:</b> head in the last {t.windowDays ?? 7} days, states, and the {t.windowDays ?? 7}-day change.</li>
            <li><b>Today Wtd. Avg:</b> today&rsquo;s sales only, averaged by head.</li>
          </ul>
          <p className="rbtx-rbix-infotip-count">Through {t.asOf}: {t.head} head in {t.lots ?? "—"} lots at {t.barns} barns.</p>
          <p className="rbtx-rbix-infotip-links"><a href={`/index/${t.slug}`}>This index <span aria-hidden="true">→</span></a> <a href="/index/methodology">How it is figured <span aria-hidden="true">→</span></a></p>
        </div>
      )}
    </div>
  );
}

/** One sentence on what the index averages: "The average price of 700–899 lb steers sold at sale
 *  barns in 11 states over the last 7 days. Each lot counts by its head. Lots under 10 head are
 *  left out." Built from the tile's numbers, so it can never disagree with the card. */
function plainAbout(t: RbIndexTile): string {
  const range = t.weightRange ?? null;
  const cattle = t.cattleLabel ?? "cattle";
  const what = !range ? cattle : /^\d/.test(range) && range.includes("–") ? `${range} ${cattle}` : `${cattle} ${range}`;
  const days = t.windowDays ?? 7;
  return `The average price of ${what} sold at sale barns in ${t.states} states over the last ${days} days. ` +
    `Each lot counts by its head, so a 100-head lot counts ten times a 10-head lot. ` +
    `Lots under ${t.minHead ?? 10} head are left out.`;
}

/** The weight range after a card's name, unless the name already says it ("Steer & Heifer 500+"). */
function nameRange(t: RbIndexTile): string | null {
  const r = t.weightRange ?? null;
  if (!r) return null;
  const lead = r.match(/^(\d+)/)?.[1];
  if (lead && r.endsWith("and over") && t.name.includes(`${lead}+`)) return null;
  return r;
}

export function IndexCards({ kpis }: { kpis: HomeKpi[] }) {
  return (
    <div className="rbtx-indexes">
      <div className="rbtx-indexes-label">Indexes</div>
      <div className="rbtx-indexes-row">
        {kpis.map((k) => (
          <div className="rbtx-index" key={k.key}>
            <div className="rbtx-index-label" title={k.label}>
              {k.label}
            </div>
            <div className="rbtx-index-row">
              <div className="rbtx-index-val">{k.value}</div>
            </div>
            <div className="rbtx-index-as">{k.asOf ? `as of ${k.asOf}` : "no data"}</div>
            {/* Underneath everything, spanning the card.
                Beside the figure it had ~20px to work in — three cards in a
                396px rail leave ~104px of content each, and a 19px "348.83"
                takes 72 of them. Given its own row it gets the full width, the
                figure keeps the artboard's size, and nothing competes.
                preserveAspectRatio="none" lets it stretch to whatever the card
                is: a sparkline's meaning is its shape, not its proportions. */}
            <svg
              className="rbtx-index-spark"
              height="22"
              viewBox="0 0 74 22"
              fill="none"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polyline
                points={sparkPoints(k.spark)}
                stroke="#CFD5D8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── The right rail ──────────────────────────────────────────────────────────

/**
 * "The packer side" — boxed beef and fed cattle, then the reporting tools.
 *
 * Framed as the packer SIDE, deliberately, not as packer tools. Cutout and fed
 * cash are upstream signals the supply chain reads to anticipate feeder demand;
 * the audience here is cow-calf through feedlot, never the packer. That is the
 * audience rule in CLAUDE.md, and it is why the copy says "where the packer's
 * revenue is" — describing a counterparty's position, which is useful to know,
 * rather than advising them.
 */
/** "▲ 1.27" / "▼ 2.43" / "unchanged". Direction is the arrow AND the colour,
 *  and the number is always unsigned so "▲ -2.43" can never be rendered. */
function MoveTag({ m, label }: { m: Move | null; label?: string }) {
  if (!m) return null;
  return (
    <span className="rbtx-move" data-dir={m.dir ?? "flat"}>
      {m.dir === null ? (
        "unchanged"
      ) : (
        <>
          {m.dir === "up" ? "▲" : "▼"} {m.value}
        </>
      )}
      {label && <span className="rbtx-move-l">{label}</span>}
    </span>
  );
}

export function TickerRail({
  boxed,
  fiveArea,
  margin,
}: {
  /** Freshest cutout with its day-over-day change, or null. */
  boxed: BoxedBeef | null;
  /** 5-Area fed cattle, weekly-led, or null. */
  fiveArea: FiveArea | null;
  /** Headline packer margin, or null when unavailable. */
  margin: PackerMargin | null;
}) {
  return (
    <>
      <div className="rbtx-raillabel">The packer side</div>

      <a className="rbtx-railcard" href="/boxed-beef">
        <span className="rbtx-railcard-top">
          <span className="rbtx-railcard-title">Boxed Beef</span>
          <Arrow />
        </span>
        <span className="rbtx-railcard-blurb">Cutout, primals and 42 cuts</span>
        {boxed && (
          <>
            <span className="rbtx-cutout">
              <span>
                <span className="rbtx-cutout-label">Choice</span>
                <span className="rbtx-cutout-val">{boxed.choice}</span>
                {/* The CHANGE, which the card used to omit entirely. On a
                    cutout the level alone does not say whether demand is
                    firming, and the two sides can disagree: 2026-09-11 was
                    Choice -2.43 with Select +1.07, i.e. the Choice-Select
                    spread narrowing, which the levels alone hide. */}
                {/* BOTH horizons, labelled. A day and a week can point
                    opposite ways and that IS the information: 2026-09-11 had
                    Choice −2.43 on the day but only −0.23 on the week, and
                    Select +1.07 on the day against −2.74 on the week. One
                    figure alone would have told a reader the wrong story
                    twice over. */}
                <MoveTag m={boxed.choiceMove} label="d/d" />
                <MoveTag m={boxed.choiceWeek} label="w/w" />
              </span>
              <span>
                <span className="rbtx-cutout-label">Select</span>
                <span className="rbtx-cutout-val">{boxed.select}</span>
                <MoveTag m={boxed.selectMove} label="d/d" />
                <MoveTag m={boxed.selectWeek} label="w/w" />
              </span>
            </span>
            {/* The SESSION, labelled. Boxed beef publishes twice a day and the
                card now takes whichever is newest — so an unlabelled figure
                would invite comparing this morning's AM with yesterday's PM as
                though they were the same series. */}
            <span className="rbtx-railcard-foot">
              {/* ⚠️ SAYS ONLY WHAT IT SHOWS (2026-09-14 launch audit). After a
                  holiday there is no report exactly a week back (Sep 7 was Labor
                  Day), so the w/w tags are absent — and the footer used to promise
                  them anyway. And the AM print is preliminary: the Boxed Beef
                  page shows only the final PM report, so a mid-day AM figure here
                  is labelled as the morning's, not passed off as the final. */}
              {boxed.session === "AM" ? "Morning (preliminary) report" : "Final report"}
              {boxed.asOf ? ` · ${boxed.asOf}` : ""} · d/d vs the prior report
              {boxed.choiceWeek || boxed.selectWeek ? " · w/w vs a week back" : ""}
            </span>
          </>
        )}
      </a>

      <a className="rbtx-railcard" href="/fed-cattle">
        <span className="rbtx-railcard-top">
          <span className="rbtx-railcard-title">Fed Cattle</span>
          <Arrow />
        </span>
        <span className="rbtx-railcard-blurb">Five Area cash and barn-vs-packer basis</span>

        {fiveArea && (
          <>
            <span className="rbtx-cutout">
              <span>
                <span className="rbtx-cutout-label">Live</span>
                <span className="rbtx-cutout-val">{fiveArea.live}</span>
                <MoveTag m={fiveArea.vsWeek} label="w/w" />
              </span>
              <span>
                <span className="rbtx-cutout-label">Dressed</span>
                <span className="rbtx-cutout-val">{fiveArea.dressed}</span>
                {/* Dressed printed bare while live carried a change, which
                    reads as "dressed did not move". It did: −0.39 on the week
                    against live's −0.19 on 2026-09-07.
                    ⚠️ NO d/d HERE. This headline is a WEEKLY weighted average
                    and a week has no yesterday. Intra-week movement is shown
                    the honest way, as today's daily below, and only when its
                    sample is material. */}
                <MoveTag m={fiveArea.vsWeekDressed} label="w/w" />
              </span>
            </span>

            {/* The head count ships WITH the price, always. It is the only way
                a reader can judge the sample, and the sample is the whole
                reason this card leads with the weekly instead of the daily. */}
            <span className="rbtx-railcard-foot">
              {/* "reported", not "week of": USDA's weekly covers the PRIOR week's
                  trade and is dated by its publish day (2026-09-14 audit). */}
              5-Area negotiated · reported {fiveArea.weekOf} · {fiveArea.head} hd
              {/* Today's daily only when its sample is material (>=5,000 head).
                  The weekly cannot show a market that breaks mid-week, but a
                  210-head day is one pen and does not belong on a front page. */}
              {fiveArea.today && (
                <>
                  {/* Not "today": the daily report is the previous trading day's trade. */}
                  {" · daily report "}
                  {fiveArea.today.price} on {fiveArea.today.head} hd
                </>
              )}
            </span>
          </>
        )}
      </a>

      {/* ── Packer Margin ───────────────────────────────────────────────────
          Third in the fed-cattle group, with Boxed Beef and Fed Cattle.

          FRAMED AS AN UPSTREAM SIGNAL, because packers are not this product's
          audience (CLAUDE.md): the question it answers is "how much room is
          behind the next bid", not "how is the packer doing". The copy is
          deliberately measured — a counterparty's position is useful to know,
          and no version of this card says anything is being taken from anyone.

          The cash-leg date is ON the card, not in a tooltip. The cash side is
          the WEEKLY comprehensive held flat for days while the cutout moves
          twice daily, so intra-week movement is cutout-only: over the three
          days to 2026-09-11 this read $389 → $367 → $343, which looks like a
          collapse and was not — week over week, $344 → $343. Without the date
          the card would invite exactly that misreading, which is why the
          comparison shown is weekly too. */}
      {/* ⚠️ /packer-margin, not /market-pulse (owner, 2026-09-13). The terminal
          page is choice-gated, so a free visitor clicking this card landed on
          /pricing. The public page shows the same view's trend and daily list. */}
      <a className="rbtx-railcard" href="/packer-margin">
        <span className="rbtx-railcard-top">
          <span className="rbtx-railcard-title">Packer Margin</span>
          <Arrow />
        </span>
        {/* ⚠️ "the room behind the next bid" STAYS. CLAUDE.md's audience rule
            is that packer margin is admissible only as an upstream signal the
            supply chain reads to anticipate the next bid — never as the
            packer's own P&L. That clause is what makes this card appropriate
            for this audience, so it survives the trim; "plus the byproduct
            drop credit" does not, because the drop credit is already
            disclosed where it matters, in the Gross/Net label below. */}
        <span className="rbtx-railcard-blurb">
          Cutout less cash — the room behind the next bid
        </span>

        {margin && (
          <span className="rbtx-cutout">
            <span>
              <span className="rbtx-cutout-label">
                {/* "Gross" when the drop credit is absent for the day. The view
                    coalesces a missing credit to zero, which understates by
                    ~$130–150/head; publishing that under the word "net" would
                    be the actual defect, so the label changes instead. */}
                {margin.gross ? "Gross / head" : "Net / head"}
              </span>
              <span className="rbtx-cutout-val">{margin.perHead}</span>
            </span>
            <span>
              <span className="rbtx-cutout-label">Per cwt</span>
              <span className="rbtx-cutout-val">{margin.perCwt}</span>
            </span>
          </span>
        )}

        {margin && (
          <span className="rbtx-margin-foot">
            <span className="rbtx-margin-cond" data-tone={margin.tone}>
              {margin.condition}
            </span>
            {margin.vsWeek && (
              <span className="rbtx-margin-vs">
                {margin.dir === null
                  ? "level on the week"
                  : `${margin.dir === "up" ? "▲" : "▼"} ${margin.vsWeek} on the week`}
              </span>
            )}
            {margin.cashAsOf && (
              <span className="rbtx-margin-as">cash as of {margin.cashAsOf}</span>
            )}
          </span>
        )}
      </a>

      {/* ⚠️ "Today's leaderboard" and "Sale reports" were HERE and were removed
          on the owner's call, 2026-09-13. The rail is THE PACKER SIDE — boxed
          beef, fed cattle, packer margin — and two feeder-market links sat
          under that heading answering a different question.

          Neither is stranded: both are in the tools lineup (tools.ts), the
          masthead nav and the footer, and the leaderboard is in the picks
          strip.

          ⚠️ Cattle on Feed was the last plain card here and came out on the
          owner's call, 2026-09-13. /cattle-on-feed is still routed and still in
          the tools lineup (tools.ts); it is no longer on the front door. */}
    </>
  );
}

function Arrow() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#C08A2E"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

// ── Bottom tool rail ────────────────────────────────────────────────────────
//
// ⚠️ "MORE TOOLS" WAS HERE AND IS GONE (owner, 2026-09-13). Six links across
// the foot of the homepage — Calf Tracker, Steer Tracker, Drought, Slaughter,
// Freight, Basis. Every one is still routed and reachable from the tools
// lineup; the homepage simply no longer ends in a row of buttons.

// ── The small-lot disclosure ────────────────────────────────────────────────

/**
 * Shown whenever a barn or state board has fallen back to every lot size.
 *
 * The homepage requires 10+ head. At a barn or state scope that floor deletes
 * whole markets — Tennessee printed 126 lots at an average lot size of 1.1 head
 * on 2026-09-12, and not one of them would have appeared. fetchScopedTicker
 * therefore drops the floor when 10+ comes back empty, and this says so.
 *
 * It has to be said, not assumed. A single at auction is a thinner, more
 * variable print than a load lot, and a reader who compared a 2-head Tennessee
 * price to a 60-head Nebraska pen as like-for-like would be misled by our
 * silence, not by the data. Pro-producer and plain: the prices are real and
 * they are the prices a small producer actually gets — they just are not
 * pen prices.
 */
/**
 * ⚠️ TWO REASONS, TWO SENTENCES — AND THE DEFAULT ONE WOULD BE A LIE ON A CULL
 * BOARD. "No pens of 10 head or more sold here" is true when the board fell
 * back because the whole sale was small. It is NOT true on the Cow/Bull tab,
 * where the floor is dropped on purpose and a ten-head cow lot may well be
 * sitting on screen. A note that explains the right thing for the wrong reason
 * is worse than no note: it teaches a man a rule the board is not following.
 */
export function SmallLotNote({
  reason = "no-large-lots",
}: {
  reason?: "no-large-lots" | "cull";
}) {
  if (reason === "cull") {
    return (
      <p className="rbtx-smalllot">
        <b>Every lot size.</b> Cows and bulls sell one at a time — 84% of cow
        lots and 81% of bull lots are a single head — so this board carries the
        whole sale rather than pens of ten and up. Holding culls to a 10-head
        floor would hide about 98% of the trade.
      </p>
    );
  }

  return (
    <p className="rbtx-smalllot">
      <b>Every lot size.</b> No pens of 10 head or more sold here on this day, so
      the board is showing the whole sale — singles and pairs included. Small
      lots bring a wider range than load lots, so read these against other
      small-lot sales rather than against a 60-head pen.
    </p>
  );
}
